import type { Metadata } from './obrSdk.ts';

/**
 * Minimal, non-secret presence marker for a player who authenticated to the
 * DCC sheets service in this Owlbear room. OBR.party.getPlayers() only returns
 * currently connected room members, so the GM can intersect this marker with
 * the server roster without storing player codes or sheet secrets in metadata.
 */
export const DCC_SHEETS_PRESENCE_KEY = 'com.lordofdisndat.dcc/sheets-presence/v1';

export interface SheetsPresence {
  playerId: string;
  label: string;
}

export function readSheetsPresence(metadata: Metadata | Record<string, unknown>): SheetsPresence | null {
  const raw = metadata[DCC_SHEETS_PRESENCE_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.playerId !== 'string' || !value.playerId.trim()) return null;
  if (typeof value.label !== 'string' || !value.label.trim()) return null;
  return { playerId: value.playerId, label: value.label };
}

export async function publishSheetsPresence(playerId: string, label: string): Promise<void> {
  // Load the browser-only SDK lazily so the pure metadata parser remains safe
  // to import in Node/Vitest, where `window` does not exist.
  const { default: OBR } = await import('@owlbear-rodeo/sdk');
  await OBR.player.setMetadata({
    [DCC_SHEETS_PRESENCE_KEY]: { playerId: playerId.trim(), label: label.trim() },
  });
}

export async function clearSheetsPresence(): Promise<void> {
  const { default: OBR } = await import('@owlbear-rodeo/sdk');
  await OBR.player.setMetadata({ [DCC_SHEETS_PRESENCE_KEY]: null });
}
